import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

export function InputSlots({ value, length, compact = false, slotLabel = 'rakam', testId = 'digit-slot', hint = '' }) {
  const compactSlots = length >= 7;
  const tightSlots = length >= 6;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: length > 8 ? 'wrap' : 'nowrap', width: '100%', maxWidth: length > 8 ? { xs: 260, sm: 420, md: 1100 } : undefined, mx: 'auto', gap: compact ? 0.5 : { xs: compactSlots ? 0.6 : 0.85, sm: 1, md: 1.25 }, mt: { xs: 0, md: 0.4 }, mb: compact ? 0.5 : { xs: 2, md: 2.5 } }}>
      {Array.from({ length }, (_, index) => (
        <Box
          key={index}
          data-testid={testId}
          role="textbox"
          aria-readonly="true"
          aria-label={`${index + 1}. ${slotLabel}${!value[index] && hint[index] ? `, örnek ${hint[index]}` : ''}`}
          sx={{
            width: compact ? { xs: 28, sm: 35, md: 42 } : { xs: compactSlots ? 35 : tightSlots ? 39 : 42, sm: 54, md: 72 },
            height: compact ? { xs: 38, sm: 45, md: 54 } : { xs: compactSlots ? 45 : tightSlots ? 49 : 52, sm: 64, md: 82 },
            boxSizing: 'border-box',
            borderRadius: { xs: 2, md: 2.8 },
            display: 'grid',
            placeItems: 'center',
            bgcolor: value[index] ? '#ffffff' : '#fbfaff',
            border: `${value[index] ? 3 : 2}px solid ${value[index] ? '#8b5cf6' : '#ded6f6'}`,
            color: '#251777',
            fontWeight: 1000,
            fontSize: compact ? { xs: 21, sm: 25, md: 30 } : { xs: compactSlots ? 23 : tightSlots ? 26 : 28, sm: 36, md: 51 },
            boxShadow: value[index] ? '0 5px 0 rgba(109,40,217,0.13)' : 'inset 0 0 0 1px rgba(255,255,255,0.72)',
          }}
        >
          {value[index] || (hint[index] ? <span aria-hidden="true" style={{ color: '#806394' }}>{hint[index]}</span> : '')}
        </Box>
      ))}
    </Box>
  );
}

export function Keypad({ disabled, onDigit, onDelete, onSubmit, submitDisabled, submitLabel = 'Gönder',
  keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'], keyLabel = 'rakamı', columns = 5 }) {
  return (
    <Box sx={{ width: '100%', maxWidth: { xs: 304, sm: 430, md: 560 }, mx: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: { xs: 0.85, sm: 1, md: 1.15 } }}>
        {keys.map((key) => (
          <ButtonBase
            key={key}
            aria-label={`${key} ${keyLabel}`}
            disabled={disabled}
            onClick={() => onDigit(key)}
            sx={{
              height: { xs: 52, sm: 62, md: 72 },
              borderRadius: { xs: '15px', sm: '17px', md: '20px' },
              bgcolor: '#ffffff',
              border: '3px solid #d8b4fe',
              color: '#251777',
              fontWeight: 1000,
              fontSize: { xs: 28, sm: 34, md: 42 },
              boxShadow: '0 4px 0 rgba(109,40,217,0.13), 0 8px 16px rgba(76,29,149,0.08)',
              '&:hover': { bgcolor: '#fdfbff' },
              '&:active': { transform: 'translateY(2px)', boxShadow: '0 2px 0 rgba(109,40,217,0.14)' },
              '&:focus-visible': { outline: '5px solid #facc15', outlineOffset: 2 },
            }}
          >
            {key}
          </ButtonBase>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.35fr', gap: { xs: 1, md: 1.3 }, mt: { xs: 1.1, md: 1.4 } }}>
        <Button
          variant="outlined"
          startIcon={<BackspaceRoundedIcon />}
          disabled={disabled}
          onClick={onDelete}
          sx={{ minHeight: { xs: 50, md: 62 }, borderRadius: 2.5, borderWidth: 3, borderColor: '#d8b4fe', color: '#4c1d95', fontWeight: 1000, fontSize: { xs: 16, md: 20 }, textTransform: 'none' }}
        >
          Sil
        </Button>
        <Button
          variant="contained"
          endIcon={<SendRoundedIcon />}
          disabled={disabled || submitDisabled}
          onClick={onSubmit}
          sx={{ minHeight: { xs: 50, md: 62 }, borderRadius: 2.5, bgcolor: '#6d45df', fontWeight: 1000, fontSize: { xs: 16, md: 20 }, textTransform: 'none', boxShadow: '0 5px 0 rgba(76,29,149,0.25)' }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Box>
  );
}
